import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { whenSeen } from '@adornis/chemistry/directives/when-seen.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { LogistikRoutes } from '@chancenlogistik/client/routes';
import { LogistikProduct, WorkshopPack } from '@chancenwerk/db/LogistikProduct.js';
import { LogistikProductAvailability } from '@chancenwerk/db/enums/LogistikEnums.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-image.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { BehaviorSubject, debounceTime, takeUntil } from 'rxjs';
import { LogistikProductFilter } from '../../../../src/db/LogistikProductFilter.js';
import { g_getProductCategoryString } from '../../../../src/logistik/client/helper.js';
import { LogistikDialogEditProduct } from '../components/dialogs/logistik-dialog-edit-product.js';
import { LogistikDialogInfoMail } from '../components/dialogs/logistik-dialog-info-mail.js';

/**
 * @customElement logistik-page-inventory
 */
@customElement('logistik-page-inventory')
export class LogistikPageInventory extends ChemistryLitElement {
  @state() private readonly _filter = new RXController(
    this,
    new LogistikProductFilter({ search: '', searchFields: ['title'] }),
  );

  private readonly _pagination = new SubscriptionPagination(
    LogistikProduct.subscribeAllLogistikProductsBySearch.bind(LogistikProduct),
    [new LogistikProductFilter({ search: '', searchFields: ['title'] })],
    {
      imageID: 1,
      title: 1,
      isPublished: 1,
      availability: 1,
      minOrderAmount: 1,
      maxOrderAmount: 1,
      availableQuantity: 1,
      orderedQuantity: 1,
      quantityWarningThreshold: 1,
      quantityWarningMail: 1,
    },
  );

  @state() private readonly _productsLoading = new RXController(this, this._pagination.loadingInitially);
  @state() private readonly _products = new RXController(this, this._pagination.data);

  @state() private readonly _sortBySelection = new RXController(this, 'createdAt');
  @state() private readonly _sortOrderSelection = new RXController(this, 'desc' as 'desc' | 'asc');

  @state() private readonly _selectedItems = new Set<LogistikProduct>();

  private readonly _textSearch = new BehaviorSubject<string | undefined>('');

  override connectedCallback() {
    super.connectedCallback();
    this._sortBySelection.observable.subscribe(sortBy => {
      this._filter.value.sortingFields = [sortBy];
      this._filter.next(this._filter.value);
    });

    this._sortOrderSelection.observable.subscribe(sortOrder => {
      this._filter.value.sortingDirections = [sortOrder];
      this._filter.next(this._filter.value);
    });

    this._textSearch.pipe(debounceTime(200), takeUntil(this.disconnected)).subscribe(search => {
      const newValue = search ?? '';
      if (this._filter.value.search !== newValue) {
        this._filter.value.search = newValue;
        this._filter.next(this._filter.value);
      }
    });

    this._filter.observable.pipe(takeUntil(this.disconnected)).subscribe(filter => {
      this._pagination.updateParameters(filter);
    });
  }

  override render() {
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header pageTitle="Lagerverwaltung" backRoute=${LogistikRoutes.landing}>
        </logistik-component-base-header>
        <cr-flex space="lg"> ${this._filterSection()} ${this._productsList()} </cr-flex>
      </logistik-component-base-page-layout>
    `;
  }

  private _filterSection() {
    return html`
      <cr-flex
        ${css({
          background: '#fff',
          borderRadius: this.spacing.sm,
          position: 'sticky',
          top: '0',
          zIndex: '1',
          overflow: 'hidden',
        })}
      >
        <cr-flex horizontal space="md" padding="sm">
          <cr-input
            placeholder="Suchen..."
            icon="search"
            flex
            @value-changed=${(e: ValueEvent<string>) => this._textSearch.next(e.detail.value)}
          ></cr-input>
          <cr-dropdown-selection
            ${css({ minWidth: '250px' })}
            .value=${this._sortBySelection.value}
            placeholder="Sortieren nach"
            .selectables=${['availableQuantity', 'orderedQuantity', '_class', 'createdAt', 'updatedAt']}
            .renderString=${(v: string) => {
              switch (v) {
                case 'availableQuantity':
                  return 'Verfügbare Menge';
                case 'orderedQuantity':
                  return 'Bestellte Menge';
                case '_class':
                  return 'Kategorie';
                case 'createdAt':
                  return 'Angelegt am';
                case 'updatedAt':
                  return 'Aktualisiert am';
                default:
                  return '';
              }
            }}
            @value-picked=${(e: ValueEvent<string>) => {
              this._sortBySelection.next(e.detail.value);
            }}
          >
          </cr-dropdown-selection>
          <cr-dropdown-selection
            ${css({ minWidth: '170px' })}
            .value=${this._sortOrderSelection.value}
            placeholder="Sortierrichtung"
            .selectables=${['asc', 'desc']}
            .renderString=${(v: string) => {
              switch (v) {
                case 'asc':
                  return 'Aufsteigend';
                case 'desc':
                  return 'Absteigend';
                default:
                  return '';
              }
            }}
            @value-picked=${(e: ValueEvent<Maybe<'asc' | 'desc'>>) => {
              if (e.detail.value) this._sortOrderSelection.next(e.detail.value);
            }}
          >
          </cr-dropdown-selection>
        </cr-flex>
        ${this._selectedItems.size
          ? html`
              <cr-flex
                horizontal
                space="md"
                crossaxis-center
                padding="sm"
                end
                ${css({ background: this.colors.primary })}
              >
                <cr-button
                  mode="text"
                  icon="close"
                  @click=${() => {
                    this._selectedItems.clear();
                    this.requestUpdate();
                  }}
                >
                  Auswahl zurücksetzen
                </cr-button>
                <cr-button
                  icon="mail"
                  @click=${async () => {
                    const success = await LogistikDialogInfoMail.showPopup({
                      props: { products: Array.from(this._selectedItems) },
                    });
                    if (success) {
                      this._selectedItems.clear();
                      this.requestUpdate();
                    }
                  }}
                >
                  Mail senden für ${this._selectedItems.size} Produkt${this._selectedItems.size > 1 ? 'e' : ''}
                </cr-button>
              </cr-flex>
            `
          : nothing}
      </cr-flex>
    `;
  }

  private _productsList() {
    if (this._productsLoading.value)
      return html` <x-flex center crossaxis-center flex> <cr-loader></cr-loader> </x-flex> `;
    if (!this._products.isLoading && !this._products.value) return html` <cr-text>Keine Produkte gefunden</cr-text> `;
    if (!this._products.value) return html` <cr-text> Etwas ist schief gelaufen </cr-text> `;
    if (!this._products.value.length)
      return html`
        <x-empty-placeholder .img=${'close'} .text=${'Es gibt noch keine Produkte in der Lagerverwaltung'}>
        </x-empty-placeholder>
      `;
    return html`
      <cr-flex space="md"> ${this._products.value.map((product, i) => this._productCard(product, i))} </cr-flex>
    `;
  }

  private _productCard(product: LogistikProduct, i: number) {
    return html`
      <cr-card
        ${css({
          width: '100%',
          ...(this._selectedItems.has(product) ? { background: this.colors.primary.shade(50) } : {}),
        })}
      >
        ${whenSeen(
          () => {
            if (i === this._pagination.data.length - 2 && i + 4 > this._pagination.totalRequested.getValue())
              this._pagination.next();
          },
          () => {},
        )}
        <cr-flex horizontal space="sm">
          <cr-image
            disabled
            mode="contain"
            .value="${product.imageID}"
            ${css({ height: '150px', width: '150px' })}
          ></cr-image>
          <cr-flex flex space="sm">
            <cr-flex horizontal space-between crossaxis-center>
              <cr-flex space="sm">
                <cr-text bold>${g_getProductCategoryString(product._class)}</cr-text>
                <cr-h3 bold>${product.title}</cr-h3>
              </cr-flex>
              <cr-flex space="sm" horizontal crossaxis-center>
                ${product.isPublished
                  ? html`
                      <cr-flex>
                        <cr-tooltip .text=${html`<cr-text>Produkt im Shop ausblenden</cr-text>`}></cr-tooltip>
                        <cr-button
                          ${css({
                            backgroundColor: 'white',
                            color: '#E28807',
                            boxShadow: 'none',
                            border: '1px solid #E28807',
                          })}
                          icon="visibility"
                          mode="icon"
                          @click=${async () => {
                            if (await ChromiumDialog.confirm(`${product.title} wirklich ausblenden?`)) {
                              product.isPublished = false;
                              await product.save();
                              ChromiumSnackbar.show('Produkt ausgeblendet');
                            }
                          }}
                        >
                        </cr-button>
                      </cr-flex>
                    `
                  : html`
                      <cr-flex>
                        <cr-tooltip .text=${html`<cr-text>Produkt im Shop anzeigen</cr-text>`}></cr-tooltip>
                        <cr-button
                          ${css({
                            backgroundColor: 'white',
                            color: '#E28807',
                            boxShadow: 'none',
                            border: '1px solid #E28807',
                          })}
                          icon="visibility_off"
                          mode="icon"
                          @click=${async () => {
                            if (await ChromiumDialog.confirm(`${product.title} wirklich einblenden?`)) {
                              product.isPublished = true;
                              await product.save();
                              ChromiumSnackbar.show('Produkt eingeblendet');
                            }
                          }}
                        >
                        </cr-button>
                      </cr-flex>
                    `}
                <cr-button
                  ${css({
                    backgroundColor: 'white',
                    color: '#E28807',
                    boxShadow: 'none',
                    border: '1px solid #E28807',
                  })}
                  icon="edit"
                  mode="icon"
                  @click=${() =>
                    LogistikDialogEditProduct.showPopup({ modal: true, props: { productID: product._id } })}
                >
                </cr-button>
                <cr-checkbox
                  @value-picked=${(e: ValueEvent<boolean>) => {
                    if (e.detail.value) this._selectedItems.add(product);
                    else this._selectedItems.delete(product);

                    this.requestUpdate();
                  }}
                  .value=${this._selectedItems.has(product)}
                >
                </cr-checkbox>
              </cr-flex>
            </cr-flex>
            <cr-flex horizontal space="md">
              <cr-flex flex space="sm">
                <cr-dropdown-selection
                  .value=${live(product.availability)}
                  placeholder="Lieferbarkeit"
                  .selectables=${Object.values(LogistikProductAvailability)}
                  .render-string=${product.availability}
                  @value-picked=${async (e: ValueEvent<LogistikProductAvailability>) => {
                    if (
                      await ChromiumDialog.confirm(
                        `Lieferbarkeit von ${product.title} wirklich auf ${e.detail.value} setzen?`,
                      )
                    ) {
                      product.availability = e.detail.value;
                      await product.save();
                      ChromiumSnackbar.show('Produkt gespeichert');
                      return;
                    }
                    this.requestUpdate();
                  }}
                ></cr-dropdown-selection>
                <cr-input
                  type="number"
                  .value=${live(product.minOrderAmount)}
                  placeholder=${product instanceof WorkshopPack ? 'Mindestteilnehmerzahl' : 'Mindestbestellmenge'}
                  @value-picked=${async (e: ValueEvent<number>) => {
                    if (
                      await ChromiumDialog.confirm(
                        `Mindestbestellmenge von ${product.title} wirklich auf ${e.detail.value} setzen?`,
                      )
                    ) {
                      product.minOrderAmount = e.detail.value;
                      await product.save();
                      ChromiumSnackbar.show('Produkt gespeichert');
                      return;
                    }
                    this.requestUpdate();
                  }}
                ></cr-input>
                <cr-input
                  type="number"
                  .value=${live(product.maxOrderAmount)}
                  placeholder=${product instanceof WorkshopPack ? 'Maximalteilnehmerzahl ' : 'Maximalbestellmenge'}
                  @value-picked=${async (e: ValueEvent<number>) => {
                    if (
                      await ChromiumDialog.confirm(
                        `Maximalbestellmenge von ${product.title} wirklich auf ${e.detail.value} setzen?`,
                      )
                    ) {
                      product.maxOrderAmount = e.detail.value;
                      await product.save();
                      ChromiumSnackbar.show('Produkt gespeichert');
                      return;
                    }
                    this.requestUpdate();
                  }}
                ></cr-input>
              </cr-flex>
              <cr-flex flex space="sm">
                <cr-input
                  type="number"
                  .value=${live(product.availableQuantity)}
                  placeholder="Anzahl verfügbarer Produkte"
                  @value-picked=${async (e: ValueEvent<number>) => {
                    if (
                      await ChromiumDialog.confirm(
                        `Anzahl verfügbarer Produkte von ${product.title} wirklich auf ${e.detail.value} setzen?`,
                      )
                    ) {
                      product.availableQuantity = e.detail.value;
                      await product.save();
                      ChromiumSnackbar.show('Produkt gespeichert');
                      return;
                    }
                    this.requestUpdate();
                  }}
                ></cr-input>
                <cr-input
                  disabled
                  type="number"
                  .value=${live(product.orderedQuantity)}
                  placeholder="Anzahl versendeter Produkte"
                ></cr-input>
                <cr-input
                  type="number"
                  .value=${live(product.quantityWarningThreshold)}
                  placeholder="Warnung bei weniger als"
                  @value-picked=${async (e: ValueEvent<number>) => {
                    if (
                      await ChromiumDialog.confirm(
                        `"Warnung bei weniger als" von ${product.title} wirklich auf ${e.detail.value} setzen?`,
                      )
                    ) {
                      product.quantityWarningThreshold = e.detail.value;
                      await product.save();
                      ChromiumSnackbar.show('Produkt gespeichert');
                      return;
                    }
                    this.requestUpdate();
                  }}
                ></cr-input>
                <cr-input
                  .value=${live(product.quantityWarningMail)}
                  placeholder="Warnung per E-Mail an"
                  @value-picked=${async (e: ValueEvent<string>) => {
                    if (
                      await ChromiumDialog.confirm(
                        `"Warnung per E-Mail an" von ${product.title} wirklich auf ${e.detail.value} setzen?`,
                      )
                    ) {
                      product.quantityWarningMail = e.detail.value;
                      await product.save();
                      ChromiumSnackbar.show('Produkt gespeichert');
                      return;
                    }
                    this.requestUpdate();
                  }}
                ></cr-input>
              </cr-flex>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }
}
